import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Drawer from "@mui/material/Drawer";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Summary from './summary';
import AllClients from './all_clients';
import { useAuth } from '../../context/AuthContext';
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import "../../styles.css";

const Clients = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const [resetAllClients, setResetAllClients] = useState(false);
  // Summary's aggregated totals, lifted up so the hero can render its own
  // stat cards without a second fetch/aggregation pass.
  const [summaryStats, setSummaryStats] = useState(null);
  // All Clients' portfolio totals, same lift-up pattern.
  const [allClientsStats, setAllClientsStats] = useState(null);
  // True while a client's Balance Sheet detail view is open (desktop) —
  // hides the page hero (title/tagline/tabs/KPI cards), which makes no
  // sense to show while you're looking at one specific client.
  const [clientDetailOpen, setClientDetailOpen] = useState(false);

  // Financial Year filter (shared with the Summary pie/table). Same behaviour as All Clients.
  const [financialYear, setFinancialYear] = useState(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-12
    const currentYear = today.getFullYear();
    if (currentMonth >= 4) {
      // April-December: FY = current year to next year
      return `${currentYear}-${String(currentYear + 1).slice(-2)}`;
    }
    // January-March: FY = previous year to current year
    return `${currentYear - 1}-${String(currentYear).slice(-2)}`;
  });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [fyAnchor, setFyAnchor] = useState(null);
  const financialYears = ["Custom Date", "2024-25", "2025-26", "2026-27"];

  // If user_type is "main", grant full access to everything
  const isMainUser = user?.user_type === 'main';

  // Check if user has access to Summary Tab and All Clients Tab (main users always have access)
  const hasSummaryAccess = isMainUser || user?.access_rights?.summaryTab !== 'no_access';
  const hasAllClientsAccess = isMainUser || user?.access_rights?.allClientsTab !== 'no_access';

  // Auto-select the first available tab
  useEffect(() => {
    // If Summary Tab is hidden, ensure we're on the first available tab
    if (!hasSummaryAccess && hasAllClientsAccess) {
      setTabValue(0); // All Clients is now at index 0
    }
    // If All Clients is hidden but Summary is visible
    else if (hasSummaryAccess && !hasAllClientsAccess) {
      setTabValue(0); // Summary is at index 0
    }
    // If both visible and tabValue is invalid, reset to 0
    else if (hasSummaryAccess && hasAllClientsAccess && tabValue > 1) {
      setTabValue(0);
    }
  }, [hasSummaryAccess, hasAllClientsAccess]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset AllClients view to table when clicking on All Clients tab
    // Adjust index based on whether Summary tab is visible
    const allClientsIndex = hasSummaryAccess ? 1 : 0;
    if (newValue === allClientsIndex) {
      setResetAllClients(prev => !prev);
    }
  };

  // Summary is at index 0 only when it's visible
  const isSummaryActive = hasSummaryAccess && tabValue === 0;
  // All Clients' index shifts depending on whether Summary is visible
  const isAllClientsActive = hasAllClientsAccess && tabValue === (hasSummaryAccess ? 1 : 0);

  const handleFinancialYearChange = (event) => {
    const selectedValue = event.target.value;
    if (selectedValue === "Custom Date") {
      setCustomDateDialogOpen(true);
    } else {
      setFinancialYear(selectedValue);
    }
  };

  const handleClearFinancialYear = () => {
    setFinancialYear("");
    setFromDate("");
    setToDate("");
  };

  const handleCustomDateSearch = () => {
    if (fromDate && toDate) {
      setFinancialYear("Custom Date");
      setCustomDateDialogOpen(false);
    }
  };

  const handleCustomDateCancel = () => {
    setCustomDateDialogOpen(false);
  };

  // Mobile FY pill dropdown
  const handleFyPillOpen = (event) => setFyAnchor(event.currentTarget);
  const handleFyPillClose = () => setFyAnchor(null);
  const handleFyPillSelect = (year) => {
    if (year === "Custom Date") {
      setCustomDateDialogOpen(true);
    } else {
      setFinancialYear(year);
    }
    setFyAnchor(null);
  };
  const fyPillLabel =
    financialYear === "Custom Date" ? "Custom" : financialYear ? `FY ${financialYear}` : "All Years";

  // Convert the selected financial year into a date range for the Summary aggregation
  const getFinancialYearRange = () => {
    if (financialYear && financialYear !== "Custom Date") {
      const [startYear, endYearShort] = financialYear.split('-');
      const endYear = `20${endYearShort}`;
      return { fromDate: `${startYear}-04-01`, toDate: `${endYear}-03-31` };
    }
    if (financialYear === "Custom Date" && fromDate && toDate) {
      return { fromDate, toDate };
    }
    return { fromDate: null, toDate: null };
  };
  const fyRange = getFinancialYearRange();

  // Shared premium stat-card renderer for the hero's right column — used by
  // both Summary and All Clients tabs, stretched to match the tabs+FY row.
  const renderHeroStatCards = (items) => (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 172px))", gridTemplateRows: "1fr", gap: 1.5 }}>
      {items.map((s) => {
        const Icon = s.icon;
        return (
          <Box
            key={s.label}
            sx={{
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              background: `linear-gradient(160deg, ${s.color}26 0%, #ffffff 62%)`,
              border: `1px solid ${s.color}3d`,
              borderRadius: "16px",
              p: "11px 14px 10px",
              boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": { transform: "translateY(-3px)", boxShadow: `0 14px 30px ${s.color}33` },
            }}
          >
            {/* decorative glow */}
            <Box sx={{ position: "absolute", top: "-24px", right: "-24px", width: "72px", height: "72px", borderRadius: "50%", background: `radial-gradient(circle, ${s.color}38 0%, transparent 72%)`, pointerEvents: "none" }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, position: "relative" }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "7px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: s.color,
                  boxShadow: `0 4px 10px ${s.color}55`,
                }}
              >
                <Icon sx={{ fontSize: 13, color: "#ffffff" }} />
              </Box>
              <Typography noWrap sx={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#5f6f86", fontFamily: "Roboto, Arial, sans-serif" }}>
                {s.label}
              </Typography>
            </Box>

            <Typography noWrap sx={{ fontSize: "16px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif", fontVariantNumeric: "tabular-nums", position: "relative" }}>
              ₹{Math.round(s.value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </Typography>

            {s.pct !== null ? (
              <Box sx={{ position: "relative" }}>
                <Box sx={{ height: "5px", borderRadius: "4px", background: "#eef2f7", overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${Math.min(100, s.pct)}%`, borderRadius: "4px", background: `linear-gradient(90deg, ${s.color}, ${s.color}bb)`, transition: "width 0.6s cubic-bezier(0.22,1,0.36,1)" }} />
                </Box>
                <Typography sx={{ fontSize: "9.5px", color: "#9aa7b8", mt: "4px", fontFamily: "Roboto, Arial, sans-serif" }}>
                  {s.pct}% {s.pctSuffix}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: "9.5px", color: "#9aa7b8", fontFamily: "Roboto, Arial, sans-serif" }}>
                {s.caption}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <div className="client-container">
      {/* ===== Desktop hero — light editorial header card ===== */}
      {/* Hidden while a client's detail view is open — the portfolio
          title/tagline/tabs/KPI cards don't apply once you've drilled in. */}
      {!clientDetailOpen && (
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          overflow: "hidden",
          borderRadius: "18px",
          background: "linear-gradient(165deg, #eaf3ff 0%, #f6fafe 55%, #ffffff 100%)",
          border: "1px solid #dce9fa",
          p: "18px 26px",
          mb: "24px",
          boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04), 0 10px 28px rgba(21, 101, 192, 0.08)",
        }}
      >
        {/* a real sky wash, top right */}
        <Box sx={{ position: "absolute", top: "-120px", right: "-60px", width: "380px", height: "380px", borderRadius: "50%", background: "radial-gradient(circle, rgba(77,178,252,0.20) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "-100px", left: "18%", width: "260px", height: "260px", borderRadius: "50%", background: "radial-gradient(circle, rgba(21,101,192,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />

        {/* Single stretched row: left column (title, then tabs+FY) and right
            column (stat cards) share the exact same height — no separate
            stacked row, no leftover vertical gap. */}
        <Box sx={{ display: "flex", alignItems: "stretch", justifyContent: "space-between", gap: 3, position: "relative" }}>
          {/* LEFT column — title on top, tabs+FY pinned to the bottom */}
          <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <Box>
              <Typography sx={{ fontSize: "24px", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.25, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>
                Clients
              </Typography>
              <Typography sx={{ fontSize: "12.5px", color: "#667085", mt: "2px", fontFamily: "Roboto, Arial, sans-serif" }}>
                Every project, every rupee — beautifully accounted for
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: "12px" }}>
              <Box sx={{ display: "inline-flex", gap: "4px", background: "#eef2f7", borderRadius: "13px", p: "4px" }}>
                {[
                  hasSummaryAccess && { label: "Summary" },
                  hasAllClientsAccess && { label: "All Clients" },
                ]
                  .filter(Boolean)
                  .map((t, idx) => {
                    const active = tabValue === idx;
                    return (
                      <Box
                        key={t.label}
                        onClick={(e) => handleTabChange(e, idx)}
                        sx={{
                          px: 2.5,
                          py: "8px",
                          borderRadius: "10px",
                          fontSize: "13px",
                          fontWeight: active ? 700 : 600,
                          fontFamily: "Roboto, Arial, sans-serif",
                          color: active ? "#1565C0" : "#5f6f86",
                          background: active ? "#ffffff" : "transparent",
                          boxShadow: active ? "0 1px 3px rgba(16,24,40,0.10)" : "none",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                          "&:hover": { color: "#1565C0" },
                        }}
                      >
                        {t.label}
                      </Box>
                    );
                  })}
              </Box>

              {/* FY selector — a real custom pill + menu, not a bordered MUI Select */}
              {isSummaryActive && (
                <>
                  <Box
                    onClick={handleFyPillOpen}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      background: fyAnchor ? "#eef6ff" : "#ffffff",
                      border: `1px solid ${fyAnchor ? "#39aafc" : "#e4e7ec"}`,
                      borderRadius: "12px",
                      px: 1.75,
                      py: "9px",
                      fontSize: "13px",
                      fontWeight: 700,
                      color: financialYear ? "#1565C0" : "#5f6f86",
                      fontFamily: "Roboto, Arial, sans-serif",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      transition: "all 0.18s ease",
                      "&:hover": { borderColor: "#39aafc", background: "#eef6ff" },
                    }}
                  >
                    <CalendarMonthIcon sx={{ fontSize: 16, color: financialYear ? "#1565C0" : "#9aa7b8" }} />
                    {fyPillLabel}
                    <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9aa7b8", transition: "transform 0.2s ease", transform: fyAnchor ? "rotate(180deg)" : "none" }} />
                  </Box>
                  <Menu
                    anchorEl={fyAnchor}
                    open={!isMobile && Boolean(fyAnchor)}
                    onClose={handleFyPillClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{ sx: { borderRadius: "14px", mt: 0.5, minWidth: 180, boxShadow: "0 12px 32px rgba(16,24,40,0.16)", border: "1px solid #eef2f6" } }}
                  >
                    {financialYears.map((year) => {
                      const selected = year === financialYear;
                      return (
                        <MenuItem
                          key={year}
                          selected={selected}
                          onClick={() => handleFyPillSelect(year)}
                          sx={{ fontSize: "13px", fontWeight: selected ? 700 : 500, color: selected ? "#1565C0" : "#344054", fontFamily: "Roboto, Arial, sans-serif", py: 1, "&.Mui-selected": { background: "#eef6ff" }, "&.Mui-selected:hover": { background: "#e2effe" } }}
                        >
                          <CheckIcon sx={{ fontSize: 16, mr: 1, visibility: selected ? "visible" : "hidden", color: "#39aafc" }} />
                          {year === "Custom Date" ? "Custom Date…" : `FY ${year}`}
                        </MenuItem>
                      );
                    })}
                    {financialYear && (
                      <MenuItem
                        onClick={() => { handleClearFinancialYear(); handleFyPillClose(); }}
                        sx={{ fontSize: "13px", color: "#d92d20", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", py: 1 }}
                      >
                        <ClearIcon sx={{ fontSize: 16, mr: 1 }} />
                        Clear filter
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          </Box>

          {/* RIGHT column — stat cards, stretched to exactly match the left
              column's height (gridTemplateRows:"1fr" + parent alignItems:"stretch").
              Same premium card renderer, fed by whichever tab is active. */}
          {isSummaryActive && summaryStats && renderHeroStatCards([
            { label: "Total Revenue", color: "#b8860b", icon: TrendingUpIcon, value: summaryStats.totalRevenue, pct: null, caption: "This financial year", pctSuffix: "of revenue" },
            { label: "Material", color: "#1E40AF", icon: Inventory2OutlinedIcon, value: summaryStats.totalMaterialPurchases, pct: summaryStats.totalRevenue > 0 ? ((summaryStats.totalMaterialPurchases / summaryStats.totalRevenue) * 100).toFixed(1) : "0.0", pctSuffix: "of revenue" },
            { label: "Labour", color: "#DB2777", icon: EngineeringOutlinedIcon, value: summaryStats.totalLabourCharges, pct: summaryStats.totalRevenue > 0 ? ((summaryStats.totalLabourCharges / summaryStats.totalRevenue) * 100).toFixed(1) : "0.0", pctSuffix: "of revenue" },
            { label: "Gross Profit", color: "#059669", icon: SavingsOutlinedIcon, value: summaryStats.netProfit, pct: summaryStats.totalRevenue > 0 ? ((summaryStats.netProfit / summaryStats.totalRevenue) * 100).toFixed(1) : "0.0", pctSuffix: "of revenue" },
          ])}

          {isAllClientsActive && allClientsStats && renderHeroStatCards([
            { label: "Contract Value", color: "#1565C0", icon: DescriptionOutlinedIcon, value: allClientsStats.sales, pct: null, caption: "Across all clients", pctSuffix: "of contract value" },
            { label: "Received", color: "#059669", icon: AccountBalanceWalletOutlinedIcon, value: allClientsStats.received, pct: allClientsStats.sales > 0 ? ((allClientsStats.received / allClientsStats.sales) * 100).toFixed(1) : "0.0", pctSuffix: "of contract value" },
            { label: "Receivable", color: "#b45309", icon: HourglassBottomOutlinedIcon, value: allClientsStats.receivable, pct: allClientsStats.sales > 0 ? ((allClientsStats.receivable / allClientsStats.sales) * 100).toFixed(1) : "0.0", pctSuffix: "of contract value" },
            { label: "Gross Profit", color: "#6d28d9", icon: SavingsOutlinedIcon, value: allClientsStats.profit, pct: allClientsStats.sales > 0 ? ((allClientsStats.profit / allClientsStats.sales) * 100).toFixed(1) : "0.0", pctSuffix: "of contract value" },
          ])}
        </Box>
      </Box>
      )}

      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          {/* Mobile keeps the original underline Tabs untouched */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              width: "fit-content",
              display: { xs: "flex", md: "none" },
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="arcdx tabs"
              TabIndicatorProps={{
                style: {
                  backgroundColor: "#39aafc",
                  height: "3px",
                  borderRadius: "3px",
                }
              }}
            >
              {/* Show Summary Tab only if user has access */}
              {hasSummaryAccess && (
                <Tab
                  label="Summary"
                  sx={{
                    textTransform: "none",
                    fontSize: { xs: "12.5px", md: "13px" },
                    fontWeight: { xs: 600, md: 500 },
                    fontFamily: "Roboto, Arial, sans-serif",
                    color: "#667085",
                    "&.Mui-selected": {
                      color: "#39aafc",
                    },
                    minHeight: "48px",
                  }}
                />
              )}
              {/* Show All Clients Tab only if user has access */}
              {hasAllClientsAccess && (
                <Tab
                  label="All Clients"
                  sx={{
                    textTransform: "none",
                    fontSize: { xs: "12.5px", md: "13px" },
                    fontWeight: { xs: 600, md: 500 },
                    fontFamily: "Roboto, Arial, sans-serif",
                    color: "#667085",
                    "&.Mui-selected": {
                      color: "#39aafc",
                    },
                    minHeight: "48px",
                  }}
                />
              )}
            </Tabs>
          </Box>

          {/* Financial Year selector — next to the All Clients tab */}
          {isSummaryActive && (
            <>
              {/* Mobile: sleek pill dropdown */}
              <Box
                onClick={handleFyPillOpen}
                sx={{
                  display: { xs: "inline-flex", md: "none" },
                  alignItems: "center",
                  gap: 0.5,
                  marginLeft: "auto",
                  background: fyAnchor ? "#e2effe" : "#eff5ff",
                  color: "#1f6fd6",
                  border: "1px solid #d6e6ff",
                  borderRadius: "20px",
                  px: 1.25,
                  py: 0.95,
                  fontSize: "12px",
                  fontWeight: 700,
                  fontFamily: "Roboto, Arial, sans-serif",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "background 0.2s ease",
                  "&:active": { transform: "scale(0.97)" },
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 16 }} />
                {fyPillLabel}
                <KeyboardArrowDownIcon sx={{ fontSize: 18, transition: "transform 0.2s ease", transform: fyAnchor ? "rotate(180deg)" : "none" }} />
              </Box>
              {/* Desktop menu now lives in the hero's own FY pill above */}
              {/* Financial Year — mobile top sheet */}
              <Drawer
                anchor="top"
                open={isMobile && Boolean(fyAnchor)}
                onClose={handleFyPillClose}
                PaperProps={{ sx: { borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px', px: 2.5, pt: 'max(18px, env(safe-area-inset-top))', pb: 2, boxShadow: '0 10px 30px rgba(16,24,40,0.15)' } }}
              >
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828', mb: 1, fontFamily: 'Roboto, Arial, sans-serif' }}>Financial Year</Typography>
                {financialYears.map((year) => {
                  const selected = year === financialYear;
                  return (
                    <Box
                      key={year}
                      onClick={() => handleFyPillSelect(year)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.4, px: 1.25, borderRadius: '14px', cursor: 'pointer', transition: 'background 0.15s ease', background: selected ? '#eff5ff' : 'transparent', '&:active': { background: '#e8f1fe' } }}
                    >
                      <Typography sx={{ flex: 1, fontSize: '14px', fontWeight: selected ? 700 : 500, color: selected ? '#1f6fd6' : '#344054', fontFamily: 'Roboto, Arial, sans-serif' }}>
                        {year === "Custom Date" ? "Custom Date…" : `FY ${year}`}
                      </Typography>
                      {selected && <CheckIcon sx={{ fontSize: 20, color: '#39aafc' }} />}
                    </Box>
                  );
                })}
                {financialYear && (
                  <Box
                    onClick={() => { handleClearFinancialYear(); handleFyPillClose(); }}
                    sx={{ py: 1.4, px: 1.25, borderRadius: '14px', cursor: 'pointer', transition: 'background 0.15s ease', '&:active': { background: '#fef3f2' } }}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#d92d20', fontFamily: 'Roboto, Arial, sans-serif' }}>Clear filter</Typography>
                  </Box>
                )}
                <Box sx={{ width: 40, height: 4, borderRadius: 2, background: '#e0e3e8', mx: 'auto', mt: 2 }} />
              </Drawer>

              {/* Desktop FY selector now lives in the hero band above */}
            </>
          )}
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{ marginTop: "20px" }}>
        {/* Both tabs visible: Summary=0, AllClients=1 */}
        {hasSummaryAccess && hasAllClientsAccess && tabValue === 0 && (
          <Summary fromDate={fyRange.fromDate} toDate={fyRange.toDate} onStatsReady={setSummaryStats} />
        )}
        {hasSummaryAccess && hasAllClientsAccess && tabValue === 1 && (
          <AllClients
            resetToTable={resetAllClients}
            accessLevel={isMainUser ? 'full_access' : user?.access_rights?.allClientsTab}
            onStatsReady={setAllClientsStats}
            onDetailViewChange={setClientDetailOpen}
          />
        )}

        {/* Only Summary visible: Summary=0 */}
        {hasSummaryAccess && !hasAllClientsAccess && tabValue === 0 && (
          <Summary fromDate={fyRange.fromDate} toDate={fyRange.toDate} onStatsReady={setSummaryStats} />
        )}

        {/* Only AllClients visible: AllClients=0 */}
        {!hasSummaryAccess && hasAllClientsAccess && tabValue === 0 && (
          <AllClients
            resetToTable={resetAllClients}
            accessLevel={isMainUser ? 'full_access' : user?.access_rights?.allClientsTab}
            onStatsReady={setAllClientsStats}
            onDetailViewChange={setClientDetailOpen}
          />
        )}

        {/* No tabs visible - show access denied message */}
        {!hasSummaryAccess && !hasAllClientsAccess && (
          <Box sx={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <Typography variant="h6">Access Denied</Typography>
            <Typography>You don't have permission to view any client data.</Typography>
          </Box>
        )}
      </Box>

      {/* Custom Date Dialog (mirrors the All Clients custom range picker) */}
      <Dialog
        open={customDateDialogOpen}
        onClose={handleCustomDateCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <CalendarMonthIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
            Custom Date Range
          </Typography>
          <Typography sx={{ fontSize: "12.5px", opacity: 0.9, fontFamily: "Roboto, Arial, sans-serif", mt: "4px" }}>
            Filter clients between two dates
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "24px !important", px: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              fullWidth
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={handleCustomDateCancel}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCustomDateSearch}
            disabled={!fromDate || !toDate}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#4db2fc,#1565C0)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(21,101,192,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#39aafc,#0d47a1)" },
              "&.Mui-disabled": { background: "#e4e7ec", color: "#98a2b3" },
            }}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Clients;
